// Build systems: grid picking, terraforming, paths (cell-based), fences (edge-based) + enclosure detection.
import * as THREE from 'three';
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { AMENITIES } from './amenityCatalog.js';
import { getBuildingFootprint } from '../world/buildingConfig.js';

export const COSTS = {
  terraform: 40,       // per cell-step of height change
  path: 60,            // per cell
  fence: 45,           // per edge
  bulldozeRefund: 0.4, // 40% back
};

const SLOPE_BUILDABLE = 0.45; // max rise/run for buildable cell

export class BuildSystem {
  constructor(world) {
    this.world = world;                 // { renderer, terrain, camera }
    this.terrain = world.terrain;
    this.scene = world.renderer.scene;
    this.camera = world.renderer.camera;
    this.dom = world.renderer.renderer.domElement;

    this.paths = new Set();             // "x,z"
    this.fences = new Map();            // "x,z,H" / "x,z,V" -> true  (H = edge along +x at cell top, V = edge along +z at cell left)
    this.enclosures = [];               // [{ id, cells: Set }]
    this._enclosureCount = 0;

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this._painting = false;
    this._flattenHeight = null;

    this._buildHelpers();
    this._bind();
  }

  // ---------- helpers / visuals ----------
  _buildHelpers() {
    // Grid overlay: wireframe over terrain geometry, shown while a tool is active.
    const wire = new THREE.LineSegments(
      new THREE.WireframeGeometry(this.terrain.geometry),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.07, depthWrite: false })
    );
    wire.position.y += 0.03;
    wire.visible = false;
    this.scene.add(wire);
    this.gridOverlay = wire;

    // Cell highlight
    const hlGeo = new THREE.PlaneGeometry(1, 1);
    hlGeo.rotateX(-Math.PI / 2);
    this.highlight = new THREE.Mesh(hlGeo, new THREE.MeshBasicMaterial({ color: 0x4ade80, transparent: true, opacity: 0.4, depthWrite: false }));
    this.highlight.visible = false;
    this.scene.add(this.highlight);

    // Dynamic meshes — cartoon path: warm sand tone with a subtle procedural speckle.
    this.pathMesh = new THREE.Mesh(new THREE.BufferGeometry(), (() => {
      const cnv = document.createElement('canvas');
      cnv.width = cnv.height = 128;
      const ctx = cnv.getContext('2d');
      ctx.fillStyle = '#ead9b0';
      ctx.fillRect(0, 0, 128, 128);
      for (let i = 0; i < 420; i++) {
        const s = 1 + Math.random() * 2.2;
        ctx.fillStyle = Math.random() < 0.5 ? 'rgba(178,152,108,0.28)' : 'rgba(255,246,222,0.3)';
        ctx.beginPath();
        ctx.arc(Math.random() * 128, Math.random() * 128, s, 0, Math.PI * 2);
        ctx.fill();
      }
      const tex = new THREE.CanvasTexture(cnv);
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95 });
    })());
    this.pathMesh.receiveShadow = true;
    this.scene.add(this.pathMesh);

    this.fenceMat = new THREE.MeshStandardMaterial({ color: 0x6b4f35, roughness: 0.8 });
    this.fenceGroup = new THREE.Group();
    this.scene.add(this.fenceGroup);

    this.enclosureMesh = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({ color: 0x60a5fa, transparent: true, opacity: 0.12, depthWrite: false }));
    this.scene.add(this.enclosureMesh);

    this._unsubStore = store.subscribe((s) => { this.gridOverlay.visible = !!s.activeTool; if (!s.activeTool) this.highlight.visible = false; });
  }

  _bind() {
    this._onPointerMove = (e) => this._onMove(e);
    this._onPointerDown = (e) => {
      if (e.button !== 0) return;
      const tool = store.get().activeTool;
      if (tool === 'enclosure') {
        const pick = this._pick(e);
        if (pick) {
          this._pendingUndo = this._snapshot(false);
          this._stampStart = { cx: pick.cx, cz: pick.cz }; this._stampEnd = { cx: pick.cx, cz: pick.cz };
        }
        return;
      }
      if (tool) {
        this._pendingUndo = this._snapshot(tool.startsWith('terraform'));
        this._painting = true; this._flattenHeight = null; this._lastPathCell = null; this._lastFenceVertex = null; this._apply(e);
      }
      else this._selectEnclosure(e);
    };
    this._onPointerUp = () => {
      if (this._stampStart && this._stampEnd) this._placeStamp(this._stampStart, this._stampEnd);
      this._stampStart = this._stampEnd = null;
      this._painting = false; this._flattenHeight = null;
      // commit undo step if this action actually changed money (i.e. something was built)
      if (this._pendingUndo) {
        if (this._pendingUndo.money !== store.get().money) {
          this._undoStack = this._undoStack || [];
          this._undoStack.push(this._pendingUndo);
          if (this._undoStack.length > 12) this._undoStack.shift();
        }
        this._pendingUndo = null;
      }
    };
    this._onKeyDown = (e) => {
      if (e.code === 'Escape') store.set({ activeTool: null });
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') this.undo();
    };
    this.dom.addEventListener('pointermove', this._onPointerMove);
    this.dom.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('keydown', this._onKeyDown);
    this._subs = [bus.on('build:undo', () => this.undo())];
  }
  dispose() {
    this._subs?.forEach((u) => u());
    this._unsubStore?.();
    this.dom.removeEventListener('pointermove', this._onPointerMove);
    this.dom.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('keydown', this._onKeyDown);
    this.scene.remove(this.gridOverlay, this.highlight, this.pathMesh, this.fenceGroup, this.enclosureMesh);
    this.gridOverlay.geometry.dispose(); this.gridOverlay.material.dispose();
    this.highlight.geometry.dispose(); this.highlight.material.dispose();
    this.pathMesh.geometry.dispose(); this.pathMesh.material.map?.dispose?.(); this.pathMesh.material.dispose();
    this.fenceGroup.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
    this.fenceMat.dispose();
    this.enclosureMesh.geometry.dispose(); this.enclosureMesh.material.dispose();
  }

  // ---------- undo (one drag/click action = one undo step) ----------
  _snapshot(withHeights) {
    return {
      paths: new Set(this.paths),
      fences: new Map(this.fences),
      enclosures: this.enclosures.map((e) => ({ id: e.id, cells: new Set(e.cells) })),
      money: store.get().money,
      heights: withHeights ? this.terrain.heights.slice() : null,
    };
  }

  undo() {
    const snap = this._undoStack?.pop();
    if (!snap) { bus.emit('ui:toast', { text: 'Nothing to undo.', kind: 'info' }); return; }
    this.paths = snap.paths;
    this.fences = snap.fences;
    this.enclosures = snap.enclosures;
    store.set({ money: snap.money });
    if (snap.heights) { this.terrain.heights.set(snap.heights); this.terrain.refresh(); }
    this._rebuildPaths();
    this._rebuildFences();
    this._rebuildEnclosureOverlay();
    bus.emit('build:enclosuresChanged', this.enclosures);
    bus.emit('ui:toast', { text: '↩️ Undone!', kind: 'info' });
  }

  // One-drag complete habitat: rectangle of fences, guaranteed closed loop.
  _placeStamp(a, b) {
    const x0 = Math.min(a.cx, b.cx), x1 = Math.max(a.cx, b.cx);
    const z0 = Math.min(a.cz, b.cz), z1 = Math.max(a.cz, b.cz);
    if (x1 - x0 < 2 || z1 - z0 < 2) { bus.emit('ui:toast', { text: 'Drag a bigger area for a habitat!', kind: 'error' }); return; }
    const edges = [];
    for (let x = x0; x <= x1; x++) {
      if (!this.fences.has(`${x},${z0},H`)) edges.push(`${x},${z0},H`);
      if (!this.fences.has(`${x},${z1 + 1},H`)) edges.push(`${x},${z1 + 1},H`);
    }
    for (let z = z0; z <= z1; z++) {
      if (!this.fences.has(`${x0},${z},V`)) edges.push(`${x0},${z},V`);
      if (!this.fences.has(`${x1 + 1},${z},V`)) edges.push(`${x1 + 1},${z},V`);
    }
    if (!this._spend(edges.length * COSTS.fence)) return;
    edges.forEach((k) => this.fences.set(k, true));
    this._rebuildFences();
    this._detectEnclosures();
  }

  // Click an enclosure (no tool active) → simple smiley summary panel.
  _selectEnclosure(e) {
    const pick = this._pick(e);
    if (!pick) return;
    const am = this.world.amenities && this.world.amenities.byCell.get(`${pick.cx},${pick.cz}`);
    if (am && am.type !== 'entry') { bus.emit('amenity:selected', { cx: am.cx, cz: am.cz }); return; }
    const enc = this.enclosures.find((en) => en.cells.has(`${pick.cx},${pick.cz}`));
    bus.emit('enclosure:selected', enc ? { id: enc.id, size: enc.cells.size } : null);
  }

  _pick(e) {
    const rect = this.dom.getBoundingClientRect();
    this.pointer.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.intersectObject(this.terrain.mesh)[0];
    if (!hit) return null;
    const t = this.terrain;
    const fx = (hit.point.x + t.worldSize / 2) / t.cellSize;
    const fz = (hit.point.z + t.worldSize / 2) / t.cellSize;
    const cx = Math.max(0, Math.min(t.cells - 1, Math.floor(fx)));
    const cz = Math.max(0, Math.min(t.cells - 1, Math.floor(fz)));
    return { point: hit.point, cx, cz, fx, fz };
  }

  cellCenter(cx, cz) {
    const t = this.terrain;
    const wx = (cx + 0.5) * t.cellSize - t.worldSize / 2;
    const wz = (cz + 0.5) * t.cellSize - t.worldSize / 2;
    return new THREE.Vector3(wx, t.heightAtWorld(wx, wz), wz);
  }

  _onMove(e) {
    const tool = store.get().activeTool;
    if (!tool) return;
    const pick = this._pick(e);
    if (!pick) { this.highlight.visible = false; return; }

    if (tool === 'fence') {
      const edge = this._nearestEdge(pick);
      this._showEdgeHighlight(edge);
    } else if (tool === 'enclosure' && this._stampStart) {
      // rectangle ghost preview while dragging
      const x0 = Math.min(this._stampStart.cx, pick.cx), x1 = Math.max(this._stampStart.cx, pick.cx);
      const z0 = Math.min(this._stampStart.cz, pick.cz), z1 = Math.max(this._stampStart.cz, pick.cz);
      const t = this.terrain, half = t.worldSize / 2;
      const wx = ((x0 + x1 + 1) / 2) * t.cellSize - half;
      const wz = ((z0 + z1 + 1) / 2) * t.cellSize - half;
      this.highlight.scale.set((x1 - x0 + 1) * t.cellSize, 1, (z1 - z0 + 1) * t.cellSize);
      this.highlight.position.set(wx, t.heightAtWorld(wx, wz) + 0.3, wz);
      this.highlight.material.color.set(0x60a5fa);
      this.highlight.visible = true;
      this._stampEnd = { cx: pick.cx, cz: pick.cz };
    } else if (tool.startsWith('animal-')) {
      // green when inside an enclosure, red outside — no guesswork
      const c = this.cellCenter(pick.cx, pick.cz);
      const t = this.terrain;
      const inEnc = this.enclosures.some((en) => en.cells.has(`${pick.cx},${pick.cz}`));
      this.highlight.scale.set(t.cellSize, 1, t.cellSize);
      this.highlight.position.set(c.x, c.y + 0.08, c.z);
      this.highlight.material.color.set(inEnc ? 0x4ade80 : 0xef4444);
      this.highlight.visible = true;
    } else if (tool.startsWith('amenity-')) {
      // footprint-sized ghost — big buildings preview their full tile area
      const def = AMENITIES[tool.slice(8)];
      const [fw, fd] = getBuildingFootprint(tool.slice(8), (def && def.fp) || [1, 1]);
      const t = this.terrain, half = t.worldSize / 2;
      const wx = (pick.cx + fw / 2) * t.cellSize - half;
      const wz = (pick.cz + fd / 2) * t.cellSize - half;
      this.highlight.scale.set(fw * t.cellSize, 1, fd * t.cellSize);
      this.highlight.position.set(wx, t.heightAtWorld(wx, wz) + 0.15, wz);
      this.highlight.material.color.set(0x4ade80);
      this.highlight.visible = true;
    } else {
      const c = this.cellCenter(pick.cx, pick.cz);
      const t = this.terrain;
      this.highlight.scale.set(t.cellSize, 1, t.cellSize);
      this.highlight.position.set(c.x, c.y + 0.08, c.z);
      const buildable = true; // paths & fences always placeable, regardless of terrain
      this.highlight.material.color.set(tool.startsWith('terraform') ? 0xfbbf24 : 0x4ade80);
      this.highlight.visible = true;
    }
    if (this._painting) this._apply(e);
  }

  _nearestEdge(pick) {
    // Snap to nearest cell edge: H edge (along x) or V edge (along z)
    const gx = pick.fx - Math.floor(pick.fx);
    const gz = pick.fz - Math.floor(pick.fz);
    const dists = [
      { d: gz, edge: { x: pick.cx, z: pick.cz, o: 'H' } },           // top
      { d: 1 - gz, edge: { x: pick.cx, z: pick.cz + 1, o: 'H' } },   // bottom
      { d: gx, edge: { x: pick.cx, z: pick.cz, o: 'V' } },           // left
      { d: 1 - gx, edge: { x: pick.cx + 1, z: pick.cz, o: 'V' } },   // right
    ];
    dists.sort((a, b) => a.d - b.d);
    return dists[0].edge;
  }

  _edgeMidpoint(edge) {
    const t = this.terrain;
    const half = t.worldSize / 2;
    let wx, wz;
    if (edge.o === 'H') { wx = (edge.x + 0.5) * t.cellSize - half; wz = edge.z * t.cellSize - half; }
    else { wx = edge.x * t.cellSize - half; wz = (edge.z + 0.5) * t.cellSize - half; }
    return new THREE.Vector3(wx, t.heightAtWorld(wx, wz), wz);
  }

  _showEdgeHighlight(edge) {
    const t = this.terrain;
    const m = this._edgeMidpoint(edge);
    this.highlight.scale.set(edge.o === 'H' ? t.cellSize : 0.25, 1, edge.o === 'H' ? 0.25 : t.cellSize);
    this.highlight.position.set(m.x, m.y + 0.1, m.z);
    this.highlight.material.color.set(0x60a5fa);
    this.highlight.visible = true;
    this._hoverEdge = edge;
  }

  // Free perimeter fence: an octagon inset from the map edge (matching the island's
  // guaranteed-land frame from terrain.islandSDF), with an opening at the entrance gate.
  addPerimeter(gateCx) {
    const t = this.terrain;
    const N = t.cells;
    const m = t.perimeterInset ?? 0;
    const C = t.cornerCut ?? 0;
    const F0 = m, F1 = N - m;
    const gateZ = F1;
    // Octagon vertex loop (clockwise); diagonals become 45° staircases that the
    // Chaikin smoothing in _rebuildFences renders as clean diagonal fence runs.
    const pts = [
      [F0 + C, F0], [F1 - C, F0],   // north
      [F1, F0 + C], [F1, F1 - C],   // NE diagonal + east
      [F1 - C, F1], [F0 + C, F1],   // SE diagonal + south
      [F0, F1 - C], [F0, F0 + C],   // SW diagonal + west
      [F0 + C, F0],                 // NW diagonal closes the loop
    ];
    const emitEdge = (x, z, nx, nz) => {
      if (nx !== x) { // H edge between vertex (min) and (min+1) at row z
        const a = Math.min(x, nx);
        if (z === gateZ && Math.abs(a - gateCx) <= 1) return; // gate opening
        this.fences.set(`${a},${z},H`, true);
      } else {
        const a = Math.min(z, nz);
        this.fences.set(`${x},${a},V`, true);
      }
    };
    for (let i = 0; i < pts.length - 1; i++) {
      let [x, z] = pts[i];
      const [tx, tz] = pts[i + 1];
      let guard = 0;
      while ((x !== tx || z !== tz) && guard++ < 4096) {
        const dx = tx - x, dz = tz - z;
        if (Math.abs(dx) >= Math.abs(dz) && dx !== 0) { const s = Math.sign(dx); emitEdge(x, z, x + s, z); x += s; }
        else if (dz !== 0) { const s = Math.sign(dz); emitEdge(x, z, x, z + s); z += s; }
      }
    }
    this._rebuildFences();
  }

  // ---------- apply tools ----------
  _apply(e) {
    const tool = store.get().activeTool;
    const pick = this._pick(e);
    if (!pick || !tool) return;
    if (tool.startsWith('terraform')) this._terraform(tool, pick);
    else if (tool === 'path') this._paintPathLine(pick);
    else if (tool === 'fence') this._paintFenceLine(pick);
    else if (tool.startsWith('animal-')) { bus.emit('build:animalPlace', { species: tool.slice(7), cx: pick.cx, cz: pick.cz }); this._painting = false; }
    else if (tool.startsWith('prop-')) {
      // props may go anywhere outside enclosures — including ON paths
      bus.emit('build:propPlace', { type: tool.slice(5), cx: pick.cx, cz: pick.cz });
      this._painting = false; // one per click
    }
    else if (tool.startsWith('amenity-')) { bus.emit('build:amenityPlace', { type: tool.slice(8), cx: pick.cx, cz: pick.cz }); this._painting = false; }
    else if (tool === 'bulldoze') this._bulldoze(pick);
  }

  // Walk orthogonally from (x,z) to (tx,tz), stepping the dominant remaining axis first.
  // Produces one clean connected line even when the pointer moves fast or diagonally.
  _walkOrtho(x, z, tx, tz, cb) {
    let guard = 0;
    while ((x !== tx || z !== tz) && guard++ < 4096) {
      const dx = tx - x, dz = tz - z;
      if (Math.abs(dx) >= Math.abs(dz) && dx !== 0) { const step = Math.sign(dx); cb(x, z, x + step, z); x += step; }
      else if (dz !== 0) { const step = Math.sign(dz); cb(x, z, x, z + step); z += step; }
    }
  }

  _paintPathLine(pick) {
    const cur = { cx: pick.cx, cz: pick.cz };
    const last = this._lastPathCell;
    if (last && (last.cx !== cur.cx || last.cz !== cur.cz)) {
      this._walkOrtho(last.cx, last.cz, cur.cx, cur.cz, (_, __, nx, nz) => {
        this._placePath({ cx: nx, cz: nz });
      });
    } else if (!last) {
      this._placePath(cur);
    }
    this._lastPathCell = cur;
  }

  _paintFenceLine(pick) {
    const V = this.terrain.cells; // vertex indices run 0..cells
    const vx = Math.max(0, Math.min(V, Math.round(pick.fx)));
    const vz = Math.max(0, Math.min(V, Math.round(pick.fz)));
    const last = this._lastFenceVertex;
    if (!last) {
      // single click: nearest edge
      this._placeFence(this._nearestEdge(pick));
    } else if (last.vx !== vx || last.vz !== vz) {
      this._walkOrtho(last.vx, last.vz, vx, vz, (ax, az, bx, bz) => {
        if (bx > ax) this._placeFence({ x: ax, z: az, o: 'H' });
        else if (bx < ax) this._placeFence({ x: bx, z: az, o: 'H' });
        else if (bz > az) this._placeFence({ x: ax, z: az, o: 'V' });
        else this._placeFence({ x: ax, z: bz, o: 'V' });
      });
    }
    this._lastFenceVertex = { vx, vz };
  }

  _spend(amount) {
    if (store.get().kidsMode) amount = Math.ceil(amount / 2); // kids mode: everything half price
    const s = store.get();
    if (s.money < amount) { bus.emit('ui:toast', { text: 'Not enough cash!', kind: 'error' }); return false; }
    store.set({ money: Math.round(s.money - amount) });
    return true;
  }

  _terraform(tool, pick) {
    const t = this.terrain;
    const v = t.verts;
    const radius = 2; // vertices
    const strength = 0.12;
    if (tool === 'terraform-flatten' && this._flattenHeight === null) this._flattenHeight = t.heightAtWorld(pick.point.x, pick.point.z);

    let totalDelta = 0;
    const cvx = Math.round(pick.fx), cvz = Math.round(pick.fz);
    const edits = [];
    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const x = cvx + dx, z = cvz + dz;
        if (x < 1 || z < 1 || x >= v - 1 || z >= v - 1) continue; // keep borders fixed
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > radius) continue;
        const fall = 1 - dist / (radius + 0.001);
        const i = z * v + x;
        let delta = 0;
        if (tool === 'terraform-raise') delta = strength * fall;
        else if (tool === 'terraform-lower') delta = -strength * fall;
        else delta = (this._flattenHeight - t.heights[i]) * 0.25 * fall;
        edits.push({ i, delta });
        totalDelta += Math.abs(delta);
      }
    }
    const cost = Math.ceil(totalDelta * COSTS.terraform);
    if (cost > 0 && !this._spend(cost)) return;
    edits.forEach(({ i, delta }) => {
      t.heights[i] = Math.max(t.waterLevel - 5, Math.min(t.maxHeight * 1.4, t.heights[i] + delta)); // dig below water allowed
    });
    t.refresh();
    this._rebuildPaths(); this._rebuildFences(); this._rebuildEnclosureOverlay();
  }

  _placePath(pick) {
    const key = `${pick.cx},${pick.cz}`;
    if (this.paths.has(key)) return;
    if (this.world.amenities?.byCell.has(key)) {
      const now = performance.now();
      if (!this._pathBlockedToastT || now - this._pathBlockedToastT > 1000) {
        this._pathBlockedToastT = now;
        bus.emit('ui:toast', { text: 'Cannot build a path through a building.', kind: 'error' });
      }
      return;
    }
    if (!this._spend(COSTS.path)) return;
    this.paths.add(key);
    this._rebuildPaths();
    bus.emit('build:pathPlaced', { cx: pick.cx, cz: pick.cz });
  }

  _placeFence(edge) {
    const key = `${edge.x},${edge.z},${edge.o}`;
    if (this.fences.has(key)) return;
    if (!this._spend(COSTS.fence)) return;
    this.fences.set(key, true);
    this._rebuildFences();
    this._detectEnclosures();
  }

  _bulldoze(pick) {
    const key = `${pick.cx},${pick.cz}`;
    let refund = 0;
    if (this.paths.delete(key)) refund += COSTS.path * COSTS.bulldozeRefund;
    if (this.world.props) refund += this.world.props.removeAt(pick.cx, pick.cz);
    if (this.world.amenities) refund += this.world.amenities.removeAt(pick.cx, pick.cz);
    // remove fences on all 4 edges of this cell
    [`${pick.cx},${pick.cz},H`, `${pick.cx},${pick.cz + 1},H`, `${pick.cx},${pick.cz},V`, `${pick.cx + 1},${pick.cz},V`].forEach((k) => {
      if (this.fences.delete(k)) refund += COSTS.fence * COSTS.bulldozeRefund;
    });
    if (refund > 0) {
      store.set({ money: Math.round(store.get().money + refund) });
      this._rebuildPaths(); this._rebuildFences(); this._detectEnclosures();
    }
  }

  // ---------- geometry rebuilds ----------
  _rebuildPaths() {
    // Smooth ribbons: path cells are chained into polylines, Chaikin-smoothed,
    // and rendered as continuous terrain-hugging bands. Junctions get rounded discs.
    const t = this.terrain;
    const half = t.worldSize / 2;
    const lift = 0.14;
    const w = t.cellSize * 0.44;
    const positions = [], indices = [], uvs = [];
    let vi = 0;
    const UV = 0.30; // world-space uv scale
    const pushV = (x, y, z) => { positions.push(x, y, z); uvs.push(x * UV, z * UV); };
    const H = (x, z) => t.heightAtWorld(x, z) + lift;
    const centerOf = (key) => {
      const [cx, cz] = key.split(',').map(Number);
      return new THREE.Vector3((cx + 0.5) * t.cellSize - half, 0, (cz + 0.5) * t.cellSize - half);
    };

    // adjacency between path cells
    const adj = new Map();
    this.paths.forEach((key) => {
      const [cx, cz] = key.split(',').map(Number);
      const nbs = [];
      for (const [dx, dz] of [[1,0],[-1,0],[0,1],[0,-1]]) {
        const nk = `${cx+dx},${cz+dz}`;
        if (this.paths.has(nk)) nbs.push(nk);
      }
      adj.set(key, nbs);
    });

    // chains between endpoints/junctions
    const usedEdges = new Set();
    const edgeId = (a, b) => a < b ? a + '|' + b : b + '|' + a;
    const chains = [];
    const walkFrom = (start) => {
      for (const nb of adj.get(start)) {
        if (usedEdges.has(edgeId(start, nb))) continue;
        const chain = [start, nb];
        usedEdges.add(edgeId(start, nb));
        let cur = nb;
        while (adj.get(cur).length === 2) {
          const next = adj.get(cur).find((v) => !usedEdges.has(edgeId(cur, v)));
          if (!next) break;
          usedEdges.add(edgeId(cur, next));
          chain.push(next);
          cur = next;
        }
        chains.push(chain);
      }
    };
    for (const [k, nbs] of adj) if (nbs.length !== 2) walkFrom(k);
    for (const [k] of adj) walkFrom(k); // loops

    // ribbon along each smoothed chain
    const buildRibbon = (pts) => {
      if (pts.length < 2) return;
      let out = pts;
      for (let it = 0; it < 2; it++) {
        if (out.length < 3) break;
        const next = [out[0]];
        for (let i = 0; i < out.length - 1; i++) {
          next.push(out[i].clone().lerp(out[i + 1], 0.25), out[i].clone().lerp(out[i + 1], 0.75));
        }
        next.push(out[out.length - 1]);
        out = next;
      }
      const row0 = vi;
      for (let i = 0; i < out.length; i++) {
        const p = out[i];
        const prev = out[Math.max(0, i - 1)], next = out[Math.min(out.length - 1, i + 1)];
        let dx = next.x - prev.x, dz = next.z - prev.z;
        const len = Math.hypot(dx, dz) || 1;
        const px = -dz / len * w, pz = dx / len * w;
        pushV(p.x + px, H(p.x + px, p.z + pz), p.z + pz);
        pushV(p.x - px, H(p.x - px, p.z - pz), p.z - pz);
        vi += 2;
      }
      for (let i = 0; i < out.length - 1; i++) {
        const r = row0 + i * 2;
        indices.push(r, r + 2, r + 1, r + 1, r + 2, r + 3);
      }
    };
    chains.forEach((chain) => buildRibbon(chain.map(centerOf)));

    // rounded discs at endpoints, junctions and isolated cells
    this.paths.forEach((key) => {
      const deg = adj.get(key).length;
      if (deg === 2) return;
      const c = centerOf(key);
      const SEG = 10;
      const c0 = vi;
      pushV(c.x, H(c.x, c.z), c.z); vi++;
      for (let sIdx = 0; sIdx <= SEG; sIdx++) {
        const ang = (sIdx / SEG) * Math.PI * 2;
        const x = c.x + Math.cos(ang) * w, z = c.z + Math.sin(ang) * w;
        pushV(x, H(x, z), z); vi++;
      }
      for (let sIdx = 0; sIdx < SEG; sIdx++) indices.push(c0, c0 + 2 + sIdx, c0 + 1 + sIdx);
    });

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    this.pathMesh.geometry.dispose();
    this.pathMesh.geometry = geo;
    this.terrain.updatePathMask(this.paths);
  }

  _rebuildFences() {
    // Smooth fence rendering: logical grid edges are chained into polylines,
    // corner-smoothed (Chaikin), then rendered as tilted rails + posts along the curve.
    this.fenceGroup.clear();
    const t = this.terrain;
    const half = t.worldSize / 2;
    if (this.fences.size === 0) return;

    // Build vertex adjacency from edges
    const adj = new Map(); // "vx,vz" -> Set of neighbor keys
    const link = (a, b) => {
      if (!adj.has(a)) adj.set(a, new Set());
      if (!adj.has(b)) adj.set(b, new Set());
      adj.get(a).add(b); adj.get(b).add(a);
    };
    this.fences.forEach((_, key) => {
      const [x, z, o] = key.split(',');
      const a = `${x},${z}`;
      const b = o === 'H' ? `${+x + 1},${z}` : `${x},${+z + 1}`;
      link(a, b);
    });

    // Extract chains (start at degree != 2 vertices; leftover cycles picked up after)
    const usedEdges = new Set();
    const edgeId = (a, b) => a < b ? a + '|' + b : b + '|' + a;
    const chains = [];
    const walkFrom = (start) => {
      for (const nb of adj.get(start)) {
        if (usedEdges.has(edgeId(start, nb))) continue;
        const chain = [start];
        let prev = start, cur = nb;
        usedEdges.add(edgeId(prev, cur));
        chain.push(cur);
        while (adj.get(cur).size === 2) {
          const next = [...adj.get(cur)].find((v) => !usedEdges.has(edgeId(cur, v)));
          if (!next) break;
          usedEdges.add(edgeId(cur, next));
          chain.push(next);
          cur = next;
        }
        chains.push(chain);
      }
    };
    for (const [v, nbs] of adj) if (nbs.size !== 2) walkFrom(v);
    for (const [v] of adj) walkFrom(v); // remaining pure loops

    // To world points + Chaikin smoothing (2 rounds, endpoints pinned)
    const toWorld = (key) => {
      const [vx, vz] = key.split(',').map(Number);
      const x = vx * t.cellSize - half, z = vz * t.cellSize - half;
      return new THREE.Vector3(x, 0, z);
    };
    const smoothChain = (pts) => {
      let out = pts;
      for (let it = 0; it < 2; it++) {
        if (out.length < 3) break;
        const next = [out[0]];
        for (let i = 0; i < out.length - 1; i++) {
          const a = out[i], b = out[i + 1];
          next.push(a.clone().lerp(b, 0.25), a.clone().lerp(b, 0.75));
        }
        next.push(out[out.length - 1]);
        out = next;
      }
      out.forEach((p) => { p.y = t.heightAtWorld(p.x, p.z); });
      return out;
    };

    // Count rails/posts first
    const railSegs = [];
    const postPts = [];
    chains.forEach((chain) => {
      const pts = smoothChain(chain.map(toWorld));
      let acc = 0;
      postPts.push(pts[0]);
      for (let i = 0; i < pts.length - 1; i++) {
        railSegs.push([pts[i], pts[i + 1]]);
        acc += pts[i].distanceTo(pts[i + 1]);
        if (acc >= t.cellSize * 0.95) { postPts.push(pts[i + 1]); acc = 0; }
      }
      postPts.push(pts[pts.length - 1]);
    });

    const railGeo = new THREE.BoxGeometry(1, 0.12, 0.08);
    const postGeo = new THREE.CylinderGeometry(0.09, 0.11, 1.6, 6);
    const railMesh = new THREE.InstancedMesh(railGeo, this.fenceMat, railSegs.length * 2);
    const postMesh = new THREE.InstancedMesh(postGeo, this.fenceMat, postPts.length);
    railMesh.castShadow = postMesh.castShadow = true;

    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), p = new THREE.Vector3();
    const xAxis = new THREE.Vector3(1, 0, 0), dir = new THREE.Vector3();
    let ri = 0;
    railSegs.forEach(([a, b]) => {
      dir.subVectors(b, a);
      const len = dir.length();
      if (len < 0.001) { for (const _ of [0, 1]) railMesh.setMatrixAt(ri++, new THREE.Matrix4().makeScale(0, 0, 0)); return; }
      dir.normalize();
      q.setFromUnitVectors(xAxis, dir);
      sc.set(len * 1.04, 1, 1); // slight overlap hides joints
      for (const yOff of [0.55, 1.1]) {
        p.set((a.x + b.x) / 2, (a.y + b.y) / 2 + yOff, (a.z + b.z) / 2);
        m.compose(p, q, sc);
        railMesh.setMatrixAt(ri++, m);
      }
    });
    let pi = 0;
    q.identity(); sc.set(1, 1, 1);
    postPts.forEach((v) => {
      p.set(v.x, v.y + 0.8, v.z);
      m.compose(p, q, sc);
      postMesh.setMatrixAt(pi++, m);
    });
    this.fenceGroup.add(railMesh, postMesh);
  }

  // ---------- enclosure detection (flood-fill from map border; unreached pockets = enclosures) ----------
  _detectEnclosures() {
    const t = this.terrain;
    const N = t.cells;
    const visited = new Uint8Array(N * N);
    const blockedN = (x, z) => this.fences.has(`${x},${z},H`);
    const blockedS = (x, z) => this.fences.has(`${x},${z + 1},H`);
    const blockedW = (x, z) => this.fences.has(`${x},${z},V`);
    const blockedE = (x, z) => this.fences.has(`${x + 1},${z},V`);

    // BFS from all border cells
    const queue = [];
    for (let i = 0; i < N; i++) {
      queue.push([i, 0], [i, N - 1], [0, i], [N - 1, i]);
    }
    queue.forEach(([x, z]) => { visited[z * N + x] = 1; });
    while (queue.length) {
      const [x, z] = queue.pop();
      const tryGo = (nx, nz, blocked) => {
        if (nx < 0 || nz < 0 || nx >= N || nz >= N || blocked || visited[nz * N + nx]) return;
        visited[nz * N + nx] = 1;
        queue.push([nx, nz]);
      };
      tryGo(x, z - 1, blockedN(x, z));
      tryGo(x, z + 1, blockedS(x, z));
      tryGo(x - 1, z, blockedW(x, z));
      tryGo(x + 1, z, blockedE(x, z));
    }

    // Group unvisited cells into regions, then match against previous enclosures
    // by cell overlap so ids stay STABLE across fence edits (animals keep their home).
    const prev = this.enclosures;
    const regions = [];
    for (let z = 0; z < N; z++) {
      for (let x = 0; x < N; x++) {
        if (visited[z * N + x]) continue;
        const cells = new Set();
        const q2 = [[x, z]];
        visited[z * N + x] = 1;
        while (q2.length) {
          const [cx, cz] = q2.pop();
          cells.add(`${cx},${cz}`);
          const tryGo = (nx, nz, blocked) => {
            if (nx < 0 || nz < 0 || nx >= N || nz >= N || blocked || visited[nz * N + nx]) return;
            visited[nz * N + nx] = 1;
            q2.push([nx, nz]);
          };
          tryGo(cx, cz - 1, blockedN(cx, cz));
          tryGo(cx, cz + 1, blockedS(cx, cz));
          tryGo(cx - 1, cz, blockedW(cx, cz));
          tryGo(cx + 1, cz, blockedE(cx, cz));
        }
        regions.push(cells);
      }
    }
    const usedPrev = new Set();
    this.enclosures = regions.map((cells) => {
      let best = null, bestOverlap = 0;
      for (const p of prev) {
        if (usedPrev.has(p.id)) continue;
        let overlap = 0;
        const [small, big] = p.cells.size < cells.size ? [p.cells, cells] : [cells, p.cells];
        small.forEach((c) => { if (big.has(c)) overlap++; });
        if (overlap > bestOverlap) { bestOverlap = overlap; best = p; }
      }
      if (best && bestOverlap > 0) { usedPrev.add(best.id); return { id: best.id, cells }; }
      return { id: ++this._enclosureCount, cells, isNew: true };
    });
    const fresh = this.enclosures.filter((e) => e.isNew);
    fresh.forEach((e) => {
      delete e.isNew;
      pushNews(`New enclosure completed — ${e.cells.size} cells. Ready for animals!`, 'success');
    });
    this._rebuildEnclosureOverlay();
    bus.emit('build:enclosuresChanged', this.enclosures);
  }

  _rebuildEnclosureOverlay() {
    const t = this.terrain;
    const half = t.worldSize / 2;
    const positions = [], indices = [];
    let vi = 0;
    const lift = 0.15;
    this.enclosures.forEach((enc) => {
      enc.cells.forEach((key) => {
        const [cx, cz] = key.split(',').map(Number);
        const x0 = cx * t.cellSize - half, z0 = cz * t.cellSize - half;
        const x1 = x0 + t.cellSize, z1 = z0 + t.cellSize;
        positions.push(
          x0, t.heightAt(cx, cz) + lift, z0,
          x1, t.heightAt(cx + 1, cz) + lift, z0,
          x1, t.heightAt(cx + 1, cz + 1) + lift, z1,
          x0, t.heightAt(cx, cz + 1) + lift, z1
        );
        indices.push(vi, vi + 2, vi + 1, vi, vi + 3, vi + 2);
        vi += 4;
      });
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setIndex(indices);
    this.enclosureMesh.geometry.dispose();
    this.enclosureMesh.geometry = geo;
  }
}
